import React from "react";
import ToolPrepSliderField from "../../../shared/ToolPrepSliderField";
import {
  BLUR_AMOUNT_MAX,
  BLUR_AMOUNT_MIN,
  BLUR_TARGET_OPTIONS,
  BLUR_TYPES,
  PIXEL_SHAPE_OPTIONS,
  blurTypeLabel,
  blurTypeOptionsForVariant,
  clampBlurAmount,
  clampPixelParam,
  createResetBlurSettings,
  createShuffledBlurSettings,
  resolveBlurTarget,
  resolveBlurToolVariant,
  resolveBlurType,
  resolvePixelShape,
} from "../../../lib/blurPrepModel";
import "../../../layout/crop/Popup/CropPrepControls.css";
import "./BlurPrepControls.css";

function ShuffleIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="16 3 21 3 21 8" />
      <line x1="4" y1="20" x2="21" y2="3" />
      <polyline points="21 16 21 21 16 21" />
      <line x1="15" y1="15" x2="21" y2="21" />
      <line x1="4" y1="4" x2="9" y2="9" />
    </svg>
  );
}

function ResetIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="1 4 1 10 7 10" />
      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
    </svg>
  );
}

/**
 * Blur type gallery + strength slider.
 * Pixel Blur: shape + cell size / gap / softness / mix.
 * Gaussian Blur: target + amount / mix / edge preserve.
 */
const BlurPrepControls = ({
  settings,
  onChange = () => {},
  disabled = false,
  toolKey = "blur",
  toolTitle = "",
}) => {
  const variant = resolveBlurToolVariant(toolKey);
  const typeOptions = blurTypeOptionsForVariant(variant);
  const showTypeGrid = typeOptions.length > 1;
  const type = resolveBlurType(settings?.type, variant.allowedTypes);
  const showPixelControls =
    Boolean(variant.showPixelControls) || type === BLUR_TYPES.PIXELATE;
  const showGaussianControls = Boolean(variant.showGaussianControls);
  const lockBlurTarget = variant.lockBlurTarget
    ? resolveBlurTarget(variant.lockBlurTarget)
    : null;
  const amount = clampBlurAmount(settings?.amount);
  const typeName = blurTypeLabel(type);
  const heading = toolTitle || variant.title;
  const pixelShape = resolvePixelShape(settings?.pixelShape);
  const pixelGap = clampPixelParam(settings?.pixelGap, 0);
  const pixelSoftness = clampPixelParam(settings?.pixelSoftness, 0);
  const pixelMix = clampPixelParam(settings?.pixelMix, 100);
  const blurTarget = resolveBlurTarget(settings?.blurTarget);
  const gaussianMix = clampPixelParam(settings?.gaussianMix, 100);
  const edgePreserve = clampPixelParam(settings?.edgePreserve, 0);

  const handleShuffle = () => {
    if (disabled) return;
    onChange(createShuffledBlurSettings(toolKey));
  };

  const handleReset = () => {
    if (disabled) return;
    onChange(createResetBlurSettings(toolKey));
  };

  return (
    <div className="blur-prep-controls crop-prep-controls">
      {heading ? (
        <div className="crop-prep-controls__section blur-prep-controls__heading">
          <div className="blur-prep-controls__heading-row">
            <span className="crop-prep-controls__section-title">{heading}</span>
            <div className="blur-prep-controls__heading-actions">
              <button
                type="button"
                className="blur-prep-controls__icon-btn"
                disabled={disabled}
                title="Shuffle settings"
                aria-label="Shuffle settings"
                onClick={handleShuffle}
              >
                <ShuffleIcon />
              </button>
              <button
                type="button"
                className="blur-prep-controls__icon-btn"
                disabled={disabled}
                title="Reset to defaults"
                aria-label="Reset to defaults"
                onClick={handleReset}
              >
                <ResetIcon />
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showTypeGrid ? (
        <div className="crop-prep-controls__section">
          <div
            className="crop-prep-controls__preset-grid blur-prep-controls__types"
            role="listbox"
            aria-label="Blur type"
          >
            {typeOptions.map((opt) => {
              const selected = type === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  aria-pressed={selected}
                  title={opt.label}
                  className={`crop-prep-controls__preset blur-prep-controls__type${
                    selected ? " is-active" : ""
                  }`}
                  disabled={disabled}
                  onClick={() => onChange({ type: opt.id })}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {showGaussianControls && !lockBlurTarget ? (
        <div className="crop-prep-controls__section">
          <span className="crop-prep-controls__section-title">Target</span>
          <div
            className="crop-prep-controls__preset-grid blur-prep-controls__types"
            role="listbox"
            aria-label="Blur target"
          >
            {BLUR_TARGET_OPTIONS.map((opt) => {
              const selected = blurTarget === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  aria-pressed={selected}
                  title={opt.label}
                  className={`crop-prep-controls__preset blur-prep-controls__type${
                    selected ? " is-active" : ""
                  }`}
                  disabled={disabled}
                  onClick={() => onChange({ blurTarget: opt.id })}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {showPixelControls ? (
        <div className="crop-prep-controls__section">
          <span className="crop-prep-controls__section-title">Shape</span>
          <div
            className="crop-prep-controls__preset-grid blur-prep-controls__types"
            role="listbox"
            aria-label="Pixel shape"
          >
            {PIXEL_SHAPE_OPTIONS.map((opt) => {
              const selected = pixelShape === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  aria-pressed={selected}
                  title={opt.label}
                  className={`crop-prep-controls__preset blur-prep-controls__type${
                    selected ? " is-active" : ""
                  }`}
                  disabled={disabled}
                  onClick={() => onChange({ pixelShape: opt.id })}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      <ToolPrepSliderField
        label={
          showPixelControls
            ? "Cell size"
            : showTypeGrid
              ? typeName
              : "Amount"
        }
        value={amount}
        min={BLUR_AMOUNT_MIN}
        max={BLUR_AMOUNT_MAX}
        unit=""
        disabled={disabled}
        onChange={(v) => onChange({ amount: clampBlurAmount(v) })}
      />

      {showGaussianControls ? (
        <>
          <ToolPrepSliderField
            label="Mix"
            value={gaussianMix}
            min={0}
            max={100}
            unit="%"
            disabled={disabled}
            onChange={(v) => onChange({ gaussianMix: clampPixelParam(v, 100) })}
          />
          <ToolPrepSliderField
            label="Edge preserve"
            value={edgePreserve}
            min={0}
            max={100}
            unit=""
            disabled={disabled}
            onChange={(v) =>
              onChange({ edgePreserve: clampPixelParam(v, 0) })
            }
          />
        </>
      ) : null}

      {showPixelControls ? (
        <>
          <ToolPrepSliderField
            label="Gap"
            value={pixelGap}
            min={0}
            max={100}
            unit=""
            disabled={disabled}
            onChange={(v) => onChange({ pixelGap: clampPixelParam(v, 0) })}
          />
          <ToolPrepSliderField
            label="Softness"
            value={pixelSoftness}
            min={0}
            max={100}
            unit=""
            disabled={disabled}
            onChange={(v) => onChange({ pixelSoftness: clampPixelParam(v, 0) })}
          />
          <ToolPrepSliderField
            label="Mix"
            value={pixelMix}
            min={0}
            max={100}
            unit="%"
            disabled={disabled}
            onChange={(v) => onChange({ pixelMix: clampPixelParam(v, 100) })}
          />
        </>
      ) : null}
    </div>
  );
};

export default BlurPrepControls;
